import axios from "axios";
import { API } from "./apiClient";

const http = axios.create({ baseURL: API, withCredentials: true });

export const TRAIL_PROPOSAL_DIFFICULTIES = [
  { value: "UNKNOWN", label: "⚪ Not sure" },
  { value: "EASY", label: "🟢 Easy" },
  { value: "INTERMEDIATE", label: "🔵 Intermediate" },
  { value: "ADVANCED", label: "🔴 Advanced" },
  { value: "EXPERT", label: "⚫ Expert" },
];

export const TRAIL_PROPOSAL_OTHER = "__OTHER__";

export const TRAIL_PROPOSAL_TYPES = [
  { value: "", label: "Not sure", help: "Leave this empty if you do not know the trail type." },
  { value: "flow", label: "Flow trail", help: "Smooth, shaped line focused on berms, rollers and rhythm." },
  { value: "downhill", label: "Downhill", help: "Gravity-first descent, often steeper or more technical." },
  { value: "freeride", label: "Freeride", help: "Gravity line with built features, jumps, drops or creative line choice." },
  { value: "singletrack", label: "Singletrack", help: "Narrow trail, usually more natural than a shaped flow line." },
  { value: "jump line", label: "Jump line", help: "Line primarily built around jumps such as tables, doubles and kickers." },
  { value: "natural", label: "Natural trail", help: "Mostly natural terrain rather than a heavily shaped bike-park line." },
  { value: "north shore", label: "North Shore / wood features", help: "Line defined by wooden riding features or North Shore elements." },
  { value: "4x", label: "4X / Bikecross", help: "Wide race-style line with rollers, berms and jumps." },
  { value: "dirt zone", label: "Dirt / jump zone", help: "Practice or dirt-jump area rather than one normal descent." },
  { value: "skills", label: "Skills / practice area", help: "Area built for practicing technique and features." },
  { value: "uphill", label: "Uphill / climb", help: "Dedicated climbing or return trail." },
  { value: "connector", label: "Connector / transfer", help: "Link between trails or riding zones." },
  { value: TRAIL_PROPOSAL_OTHER, label: "Other / suggest new…" },
];

export const TRAIL_PROPOSAL_RIDING_STYLES = [
  { value: "", label: "Not sure", help: "Leave this empty if the riding character is unclear." },
  { value: "flow", label: "Flow", help: "Smooth rhythm, berms and rollers." },
  { value: "downhill", label: "Downhill", help: "Gravity-focused descending." },
  { value: "freeride", label: "Freeride", help: "Built features, drops, jumps and creative lines." },
  { value: "jump", label: "Jump", help: "Airtime and jump progression are the main character." },
  { value: "technical", label: "Technical", help: "Roots, rocks, steep or precise riding." },
  { value: "enduro", label: "Enduro", help: "Natural or technical descending typical of enduro riding." },
  { value: "family", label: "Family", help: "Designed to be approachable for families and less experienced riders." },
  { value: "progression", label: "Progression", help: "Built to learn and advance riding skills." },
  { value: "race", label: "Race", help: "Race-oriented line where speed and competition are central." },
  { value: "xc", label: "XC", help: "Cross-country style riding." },
  { value: TRAIL_PROPOSAL_OTHER, label: "Other / suggest new…" },
];

export const TRAIL_PROPOSAL_SURFACES = [
  { value: "", label: "Not sure", help: "Leave this empty if the surface is unknown or mixed beyond recognition." },
  { value: "natural", label: "Natural", help: "Natural forest or mountain ground." },
  { value: "dirt / earth", label: "Dirt / earth", help: "Mostly shaped soil or compacted earth." },
  { value: "gravel", label: "Gravel", help: "Predominantly gravel or crushed-stone surface." },
  { value: "roots / rocks", label: "Roots / rocks", help: "Natural technical surface dominated by roots and/or rock." },
  { value: "wood / north shore", label: "Wood / North Shore", help: "Wooden structures form a meaningful part of the riding surface." },
  { value: "paved / asphalt", label: "Paved / asphalt", help: "Predominantly hard paved surface." },
  { value: "mixed", label: "Mixed", help: "Several substantial surface types with no single dominant one." },
  { value: TRAIL_PROPOSAL_OTHER, label: "Other / suggest new…" },
];

export const emptyTrailProposal = () => ({
  name: "",
  difficulty: "UNKNOWN",
  lengthM: "",
  elevationGainM: "",
  elevationLossM: "",
  trailType: "",
  trailTypeOther: "",
  ridingStyle: "",
  ridingStyleOther: "",
  surface: "",
  surfaceOther: "",
  description: "",
  reason: "",
  sourceUrl: "",
  sourceNote: "",
  contactName: "",
  contactEmail: "",
});

export const resolveTrailProposalChoice = (value, otherValue) => {
  if (value === TRAIL_PROPOSAL_OTHER) return String(otherValue || "").trim() || TRAIL_PROPOSAL_OTHER;
  return String(value || "").trim() || null;
};

const optionalNumber = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  return Number(normalized);
};

export const normalizeTrailProposal = ({ destinationId, form }) => {
  const lengthM = optionalNumber(form.lengthM);
  return {
    destination_id: destinationId,
    name: String(form.name || "").trim(),
    difficulty: form.difficulty || "UNKNOWN",
    length_km: lengthM == null ? null : lengthM / 1000,
    elevation_gain_m: optionalNumber(form.elevationGainM),
    elevation_loss_m: optionalNumber(form.elevationLossM),
    trail_type: resolveTrailProposalChoice(form.trailType, form.trailTypeOther),
    riding_style: resolveTrailProposalChoice(form.ridingStyle, form.ridingStyleOther),
    surface: resolveTrailProposalChoice(form.surface, form.surfaceOther),
    description: String(form.description || "").trim() || null,
    reason: String(form.reason || "").trim(),
    source_url: String(form.sourceUrl || "").trim() || null,
    source_note: String(form.sourceNote || "").trim() || null,
    contact_name: String(form.contactName || "").trim() || null,
    contact_email: String(form.contactEmail || "").trim() || null,
  };
};

export const validateTrailProposal = (input) => {
  if (!input.destination_id) return "Destination is required.";
  if (!input.name) return "Trail name is required.";
  if (!input.reason) return "Tell us why this trail should be added.";
  for (const [label, value] of [
    ["Trail type", input.trail_type],
    ["Riding style", input.riding_style],
    ["Surface", input.surface],
  ]) {
    if (value === TRAIL_PROPOSAL_OTHER) return `Enter the proposed ${label.toLowerCase()}.`;
  }
  for (const [label, value] of [
    ["Length", input.length_km],
    ["Elevation gain", input.elevation_gain_m],
    ["Elevation loss", input.elevation_loss_m],
  ]) {
    if (value != null && (!Number.isFinite(value) || value < 0)) return `${label} must be a positive number.`;
  }
  return null;
};

export async function submitTrailProposal(input) {
  const response = await http.post("", {
    query: `mutation SubmitTrailProposal($input: TrailProposalInput!) {
      submitTrailProposal(input: $input) { id destination_id status submitted_at }
    }`,
    variables: { input },
  });
  if (response.data.errors?.length) throw new Error(response.data.errors[0].message);
  return response.data.data.submitTrailProposal;
}
